import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import { num } from '@/utils/format';

interface LowRow {
  id: number;
  name: string;
  sku?: string;
  stock: number;
  min_stock_qty: number;
  brand?: { name: string };
  category?: { name: string };
}

export function LowStockPage() {
  const query = useQuery({
    queryKey: ['stock', 'low'],
    queryFn: () => apiGet<{ items: LowRow[] } | LowRow[]>(endpoints.stockLow),
  });

  const rows = Array.isArray(query.data)
    ? query.data
    : query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Low stock"
        description="Items at or under their re-order threshold."
      />

      {query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : !query.isLoading && rows.length === 0 ? (
        <EmptyState
          title="Fully stocked!"
          description="Nothing crossed its low-stock threshold. Great inventory health."
          icon={<AlertTriangle className="h-6 w-6" />}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={query.isLoading}
          rows={rows}
          columns={[
            {
              key: 'name',
              header: 'Product',
              cell: (r) => (
                <Link
                  to={`/products/${r.id}`}
                  className="font-medium hover:text-brand-700"
                >
                  {r.name}
                </Link>
              ),
            },
            {
              key: 'sku',
              header: 'SKU',
              hideOnMobile: true,
              cell: (r) => (
                <span className="font-mono text-xs">{r.sku ?? '—'}</span>
              ),
            },
            {
              key: 'cat',
              header: 'Category',
              hideOnMobile: true,
              cell: (r) => r.category?.name ?? '—',
            },
            {
              key: 'min',
              header: 'Min',
              hideOnMobile: true,
              className: 'text-right',
              cell: (r) => num(r.min_stock_qty),
            },
            {
              key: 'stock',
              header: 'In stock',
              className: 'text-right',
              cell: (r) =>
                r.stock <= 0 ? (
                  <Pill tone="danger">Out</Pill>
                ) : (
                  <Pill tone="warn">{num(r.stock)}</Pill>
                ),
            },
          ]}
        />
      )}
    </>
  );
}
